import React from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';

import ArrayEditor from '../editors/ArrayEditor';
// eslint-disable-next-line import-access/jsdoc -- ShapeEditor is package-private but ui-schemas is part of the same package
import ShapeEditor from '../editors/ShapeEditor';
import {getConfigShapeAtConfigShapePath} from '../step-config-settings/StepConfigSettingsUtils';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  BACKEND_FIELD_GENERATOR_CONFIG,
  CustomComponentEditorProps,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
} from '../StepConfigEditorTypes';

function OpentronsRuntimeParametersEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const runtimeParamsShape = getConfigShapeAtConfigShapePath(['runtimeParameters'], configShape, config);

  if (runtimeParamsShape.kind === 'shape') {
    const shapeWithMeta = runtimeParamsShape as Record<string, unknown>;
    const fieldOrder =
      (shapeWithMeta.__fieldOrder as Array<string> | undefined) ?? Object.keys(runtimeParamsShape.fields);
    const displayNames = (shapeWithMeta.__displayNames as Record<string, string> | undefined) ?? {};

    if (fieldOrder.length === 0) {
      return <></>;
    }

    return (
      <ShapeEditor
        configShape={configShape}
        config={config}
        onChangeConfig={onChangeConfig}
        disabled={disabled}
        uiSchema={{
          [FIELD_ORDER_UI_SCHEMA_KEY]: fieldOrder.map((name) => `runtimeParameters_${name}`),
          ...Object.fromEntries(
            fieldOrder.map((name) => [
              `runtimeParameters_${name}`,
              {
                displayName: displayNames[name] ?? name,
                configShapePath: ['runtimeParameters', name],
              },
            ])
          ),
        }}
      />
    );
  }

  if (runtimeParamsShape.kind === 'array' && Array.isArray(config.runtimeParameters)) {
    return (
      <ArrayEditor
        config={config}
        configShape={runtimeParamsShape}
        onChangeConfig={onChangeConfig}
        disabled={disabled}
        configShapePath={['runtimeParameters']}
        elementUISchema={{
          [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'value'],
          [NUM_COLUMNS_KEY]: 2,
          name: {
            displayName: 'Parameter',
            configShapePath: ['name'],
          },
          value: {
            displayName: 'Value',
            configShapePath: ['value'],
          },
        }}
      />
    );
  }

  return <></>;
}

export const RUN_OPENTRONS_PROTOCOL_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_OPENTRONS_PROTOCOL,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId', 'protocolId', 'runtimeParameters'],
  [BACKEND_FIELD_GENERATOR_CONFIG]: {
    triggerFields: ['connectionId', 'protocolId'],
    hasSavedShape: true,
    connectionField: 'connectionId',
    liveFields: ['protocolId', 'runtimeParameters'],
    liveLoadingMessages: {
      onOpen: 'Loading protocols from Opentrons…',
      byTriggerField: {protocolId: (protocolName) => `Loading parameters for ${protocolName}…`},
    },
  },
  connectionId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId'],
    connectionId: {
      displayName: 'Connection',
      infoText: 'The instrument connection to the Opentrons robot.',
      configShapePath: ['connectionId'],
    },
  },
  protocolId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['protocolId'],
    protocolId: {
      displayName: 'Protocol',
      infoText:
        'The Opentrons protocol to run. Selecting a protocol will populate any available runtime parameters.',
      configShapePath: ['protocolId'],
    },
  },
  runtimeParameters: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['runtimeParameters'],
    runtimeParameters: {
      displayName: 'Runtime parameters',
      infoText: 'Parameters defined by the selected protocol. Values are sent to the robot at run time.',
      [CUSTOM_COMPONENT_KEY]: OpentronsRuntimeParametersEditor,
    },
  },
};
